/*****************************************************************
 *  CUSTOMER BUTLER - THE HR BUTLER CHROME (the HR look's frame)   *
 *  File: apps/web/src/pages/customer/HrChrome.js                  *
 *  What it does: everything around a Customer Butler page in the  *
 *  HR look, copied from HR Butler's own page:                     *
 *    HrHeaderRow   ONE slim row: "<- Hub", the people icon +      *
 *                  "Customer Butler"; on the right Match my phone,*
 *                  English and Help                               *
 *    HrModuleChips Customers / Properties / CRM Setup as HR's     *
 *                  filter chips (the open one filled)             *
 *    HrFooter      the "Customer Butler - v0.2.0 - built Oct      *
 *                  2026" stamp and, under it, the small muted     *
 *                  links to the earlier looks                     *
 *    HrPageFrame   all three around a page                        *
 *    HrBanner      HR's banner card (icon + plain sentence)       *
 *    HrQuietLine   HR's one-line muted sentence                   *
 *  Reads: the person's Customer Butler level (to offer CRM Setup  *
 *  to managers only).  Writes: the look / language / skin choices *
 *  on this device only.                                           *
 *****************************************************************/

/* WHY (2026-10-15, 01:04 ET, James, with two screenshots): "what you made vs
   the design spec" - the design spec is HR Butler at butlercore.com/hr/.
   Customer Butler must look and be structured EXACTLY like HR Butler: one
   slim header row, a white page with thin-lined cards, no suite bar, no
   second module bar, no breadcrumb, no alpha banner, and the look switch as
   a small muted line at the very bottom. THE WALL: HR Butler's code is read
   for its pattern and never imported; every value comes from the shared
   butler-tokens.css. */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE HEADER ROW                                     *
 *  SECTION 2 - THE MODULE CHIPS                                   *
 *  SECTION 3 - THE FOOTER (stamp + earlier looks)                 *
 *  SECTION 4 - BANNERS AND QUIET LINES                            *
 *  SECTION 5 - THE PAGE FRAME                                     *
 *  SECTION 6 - "AM I INSIDE THE HR LOOK?" (for CRM Setup)         *
 *****************************************************************/

import React, { createContext, useContext, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Icon } from './customerIcons';
import { useButlerLook, SKIN_CLEAN, SKIN_CLASSIC } from './customerLook';
import { useChosenLook, useChosenLanguage, LOOK_CHOICES, LANGUAGE_CHOICES } from './customerChromeLook';
import { useCustomerAccessView } from './customerAccessView';
import './customer.css';

/*****************************************************************
 *  SECTION 1 - THE HEADER ROW                                     *
 *****************************************************************/

const MODULE_NAME = 'Customer Butler';          // the name beside the icon
const HELP_ADDRESS = '/howto/customer.html';    // the Customer Butler guide

// WHY (2026-10-15): the suite's own help script (/app/butler-help.js) finds a
// page's header and puts ONE Help control in it - the same mechanism HR uses.
// Our link carries the id that script looks for, so the script adopts it
// instead of adding a second one. The script may point the address at the
// help index (?from=this page); a plain tap still opens the Customer Butler
// guide itself, because the tap goes to HELP_ADDRESS below.
const SUITE_HELP_BUTTON_ID = 'bc-help-button';

/* ---- openCustomerGuide(clickEvent) -------------------------------
   A plain tap opens the Customer Butler guide. A tap with Ctrl,
   Cmd, Shift or the middle button is left to the browser (new tab). */
function openCustomerGuide(clickEvent) {
  const wantsNewTab = clickEvent.ctrlKey || clickEvent.metaKey || clickEvent.shiftKey || clickEvent.button !== 0;

  if (wantsNewTab) {
    return;
  }

  clickEvent.preventDefault();
  window.location.assign(HELP_ADDRESS);
}

/* ---- HubLink() -- "<- Hub", top left, back to the Butler hub ---- */
function HubLink() {
  return (
    <Link className="cb-hr-hub" to="/" aria-label="Back to the Butler hub" title="Back to the Butler hub">
      <Icon name="arrowleft" />
      <span>Hub</span>
    </Link>
  );
}

/* ---- LookPicker() -- Match my phone / Light / Dark / High contrast ---- */
function LookPicker() {
  const [lookKey, chooseLook] = useChosenLook();

  return (
    <>
      <label className="cb-hidden-label" htmlFor="cb-hr-look">Look</label>
      <select
        id="cb-hr-look"
        className="cb-hr-look"
        value={lookKey}
        onChange={(changeEvent) => chooseLook(changeEvent.target.value)}
      >
        {LOOK_CHOICES.map((oneChoice) => (
          <option key={oneChoice.key} value={oneChoice.key}>{oneChoice.words}</option>
        ))}
      </select>
    </>
  );
}

/* ---- LanguagePicker() -- only languages with every word behind them ---- */
function LanguagePicker() {
  const [languageKey, chooseLanguage] = useChosenLanguage();

  return (
    <>
      <label className="cb-hidden-label" htmlFor="cb-hr-lang">Language</label>
      <select
        id="cb-hr-lang"
        className="cb-hr-look"
        value={languageKey}
        onChange={(changeEvent) => chooseLanguage(changeEvent.target.value)}
      >
        {LANGUAGE_CHOICES.map((oneChoice) => (
          <option key={oneChoice.key} value={oneChoice.key}>{oneChoice.words}</option>
        ))}
      </select>
    </>
  );
}

/* ---- removeFloatingHelpCopies() ---------------------------------
   The suite help script can run before React has drawn this header;
   it then pins a floating "Help" pill to the page. Once our header's
   Help is on screen that pill is a second copy, so it is taken away
   (the script adopts ours from then on). Only that pill is touched. */
function removeFloatingHelpCopies() {
  const floatingCopies = document.querySelectorAll('.bc-helpbtn-float');

  floatingCopies.forEach((oneCopy) => {
    const isOurOwn = oneCopy.classList.contains('cb-hr-help');

    if (!isOurOwn && oneCopy.parentNode) {
      oneCopy.parentNode.removeChild(oneCopy);
    }
  });
}

// how long to keep checking for a late floating copy (the suite script
// re-checks for 5 seconds after a page opens)
const HELP_CHECK_TIMES_MS = [0, 500, 1500, 3000, 6000];

/* ---- HelpLink() -- the one Help control, icon + word ---- */
function HelpLink() {
  useEffect(() => {
    const checkTimers = HELP_CHECK_TIMES_MS.map((waitMs) => setTimeout(removeFloatingHelpCopies, waitMs));

    return () => checkTimers.forEach((oneTimer) => clearTimeout(oneTimer));
  }, []);

  return (
    <a
      id={SUITE_HELP_BUTTON_ID}
      className="bc-helpbtn cb-hr-help"
      data-bc-help="1"
      href={HELP_ADDRESS}
      title="Help for Customer Butler"
      onClick={openCustomerGuide}
    >
      <Icon name="help" />
      <span>Help</span>
    </a>
  );
}

/* ---- HrHeaderRow() -----------------------------------------------
   HR's header: Hub, a hairline, the icon and the name; a spacer;
   the look, the language and Help. Not sticky (HR's ruling: a
   pinned bar with only a name in it costs a thumb of screen).   */
export function HrHeaderRow() {
  return (
    <header className="cb-hr-top">
      <HubLink />
      <div className="cb-hr-brand">
        <Icon name="people" size="button" />
        {MODULE_NAME}
      </div>
      <div className="cb-hr-top-spacer" />
      <LookPicker />
      <LanguagePicker />
      <HelpLink />
    </header>
  );
}

/*****************************************************************
 *  SECTION 2 - THE MODULE CHIPS                                   *
 *  HR's filter chips (Everybody / Working here / ...) carry the   *
 *  module's three places. The open place is filled, like HR's     *
 *  chosen chip.                                                   *
 *****************************************************************/

// WHY (2026-10-14, Honest UI, James via Fable): CRM Setup is offered only to
// a Customer Butler manager; the server refuses setup changes to everyone else.
export const MODULE_CHIPS = [
  { to: '/customers', words: 'Customers', icon: 'people', managersOnly: false },
  { to: '/properties', words: 'Properties', icon: 'home', managersOnly: false },
  { to: '/crm-setup', words: 'CRM Setup', icon: 'gear', managersOnly: true },
];

/* ---- HrModuleChips() -- the one chip row under the header ---- */
export function HrModuleChips() {
  const accessView = useCustomerAccessView(); // what this person may be shown

  const shownChips = MODULE_CHIPS.filter((oneChip) => !oneChip.managersOnly || accessView.mayChangeSetup);

  return (
    <nav className="cb-hr-tabs cb-hr-places" aria-label="Customer Butler">
      {shownChips.map((oneChip) => (
        <NavLink
          key={oneChip.to}
          to={oneChip.to}
          className={({ isActive }) => (isActive ? 'cb-hr-tab is-chosen' : 'cb-hr-tab')}
        >
          <Icon name={oneChip.icon} />
          {oneChip.words}
        </NavLink>
      ))}
    </nav>
  );
}

/*****************************************************************
 *  SECTION 3 - THE FOOTER                                         *
 *****************************************************************/

// The stamp at the foot of the page, HR's "HR Butler - v2.1.0 - built Aug 2026".
// v0.2.0: the module was 0.1.0 (server manifest); the HR look is its next look.
export const FOOTER_STAMP = 'Customer Butler · v0.2.0 · built Oct 2026';

/* ---- HrFooter({ onChooseSkin }) ----------------------------------
   The stamp, then the earlier looks as small muted links (skins
   are only ever added: both earlier looks stay one tap away).   */
export function HrFooter({ onChooseSkin }) {
  return (
    <footer className="cb-hr-foot">
      <p className="bc-stamp">{FOOTER_STAMP}</p>
      {onChooseSkin ? (
        <p className="cb-hr-looks">
          <button type="button" className="cb-hr-look-link" onClick={() => onChooseSkin(SKIN_CLASSIC)}>
            Switch to the classic look
          </button>
          <span aria-hidden="true"> · </span>
          <button type="button" className="cb-hr-look-link" onClick={() => onChooseSkin(SKIN_CLEAN)}>
            Switch to the Oct 14 clean look
          </button>
        </p>
      ) : null}
    </footer>
  );
}

/*****************************************************************
 *  SECTION 4 - BANNERS AND QUIET LINES                            *
 *  HR's two ways to say something: a banner card (a coloured left *
 *  edge, an icon and a plain sentence) for status that matters,   *
 *  and a one-line muted sentence for "nothing here yet".          *
 *****************************************************************/

// Which HR banner each tone is. Colour is never the only signal: the icon
// and the words carry the meaning too.
const BANNER_TONES = {
  ok: 'cb-hr-alarm is-ok',
  warn: 'cb-hr-alarm is-warn',
  bad: 'cb-hr-alarm is-bad',
  brand: 'cb-hr-alarm is-brand',
  quiet: 'cb-hr-alarm is-quiet',
};

/* ---- HrBanner({ tone, icon, title, children, action }) -----------
   In: tone ok|warn|bad|brand|quiet; an icon name; an optional bold
   title; the sentence; an optional action (a link or button).   */
export function HrBanner({ tone, icon, title, children, action }) {
  const bannerClass = BANNER_TONES[tone] || BANNER_TONES.quiet;

  return (
    <div className={bannerClass} role="status">
      <div className="cb-hr-alarm-top">
        <span className="cb-hr-alarm-icon"><Icon name={icon || 'check'} size="button" /></span>
        <div className="cb-hr-alarm-words">
          {title ? <p className="cb-hr-alarm-head">{title}</p> : null}
          {children ? <p className="cb-hr-alarm-body">{children}</p> : null}
          {action ? <div className="cb-hr-alarm-acts">{action}</div> : null}
        </div>
      </div>
    </div>
  );
}

/* ---- HrQuietLine({ icon, children }) -- one muted sentence ---- */
export function HrQuietLine({ icon, children }) {
  return (
    <p className="cb-hr-quiet">
      {icon ? <Icon name={icon} /> : null}
      <span>{children}</span>
    </p>
  );
}

/*****************************************************************
 *  SECTION 5 - THE PAGE FRAME                                     *
 *****************************************************************/

/* ---- HrPageFrame({ children, onChooseSkin, showPlaces }) ---------
   In: the page; onChooseSkin (to offer the earlier looks); and
   showPlaces (false on the plain "no access" pages, which have
   no places to offer).
   Out: header row, chip row, the page, the footer - on the white
   full-width ground HR uses. App.js has already left out the
   suite bars for these addresses (customerLook.js section 3).   */
export function HrPageFrame({ children, onChooseSkin, showPlaces }) {
  useButlerLook();

  const isShowingPlaces = showPlaces !== false;

  return (
    <div className="cb-page cb-hr bc-page">
      <HrHeaderRow />
      {isShowingPlaces ? <HrModuleChips /> : null}
      <main className="cb-hr-main">
        {children}
      </main>
      <HrFooter onChooseSkin={onChooseSkin} />
    </div>
  );
}

/*****************************************************************
 *  SECTION 6 - "AM I INSIDE THE HR LOOK?"                         *
 *  CRM Setup is an older page shared by every look. Inside the    *
 *  HR frame it leaves out its own module menu and narrow column   *
 *  and paints its cards with the standard's tokens.               *
 *****************************************************************/

const HrLookContext = createContext(false); // true only inside HrSetupFrame

/* ---- useIsInsideHrLook() -- true when the HR frame is around the page ---- */
export function useIsInsideHrLook() {
  return useContext(HrLookContext);
}

/* ---- HrSetupFrame({ children, onChooseSkin }) -- CRM Setup in the HR look ---- */
export function HrSetupFrame({ children, onChooseSkin }) {
  return (
    <HrPageFrame onChooseSkin={onChooseSkin}>
      <HrLookContext.Provider value>
        {children}
      </HrLookContext.Provider>
    </HrPageFrame>
  );
}

export default HrPageFrame;
